import type { IsoDate } from './common';

/** GS1 check digit: mod 10 with weights 3,1,3,… from the right of the body. Mirrors app.is_valid_gtin(). */
export function gs1CheckDigit(body: string): number {
  let sum = 0;
  for (let i = 0; i < body.length; i++) {
    const digit = body.charCodeAt(body.length - 1 - i) - 48;
    sum += digit * (i % 2 === 0 ? 3 : 1);
  }
  return (10 - (sum % 10)) % 10;
}

/** GTIN-8, -12, -13 or -14 with a valid check digit. */
export function isValidGtin(code: string): boolean {
  return /^(\d{8}|\d{12,14})$/.test(code) && gs1CheckDigit(code.slice(0, -1)) === Number(code.slice(-1));
}

/** ITF-14 of a case (koli): indicator digit + first 12 digits of the unit GTIN-13 + new check digit. */
export function itf14FromGtin13(gtin13: string, indicator = 1): string {
  const body = `${indicator}${gtin13.slice(0, 12)}`;
  return `${body}${gs1CheckDigit(body)}`;
}

/** The unit GTIN-13 a GTIN-14 was built from: drop the indicator digit, recompute the check digit. */
export function baseGtin13(gtin14: string): string {
  const body = gtin14.slice(1, 13);
  return `${body}${gs1CheckDigit(body)}`;
}

/** Fields read from a GS1-128 / GS1 DataMatrix element string (koli etiketi). */
export interface Gs1Fields {
  /** AI 01 */
  readonly gtin?: string;
  /** AI 10, parti / lot */
  readonly lot?: string;
  /** AI 21 */
  readonly serial?: string;
  /** AI 11 */
  readonly productionDate?: IsoDate;
  /** AI 15, best before (TETT) */
  readonly bestBefore?: IsoDate;
  /** AI 17, expiry (SKT) */
  readonly expiry?: IsoDate;
  /** AI 37, units in the logistic unit */
  readonly count?: number;
}

const GROUP_SEPARATOR = '\u001d';
const FIXED_LENGTH: Readonly<Record<string, number>> = {
  '00': 18,
  '01': 14,
  '02': 14,
  '11': 6,
  '12': 6,
  '13': 6,
  '15': 6,
  '16': 6,
  '17': 6,
  '20': 2,
};
const VARIABLE_LENGTH = new Set(['10', '21', '37']);

/**
 * Parses GS1 element strings in either form a scanner may deliver:
 *  - human readable: "(01)08685283266927(17)281031(10)L2610"
 *  - raw: "]C1" symbology prefix and/or FNC1 as ASCII 29 between variable-length fields
 * Returns null for ordinary barcodes (EAN-13, ITF-14, Code 128 SKUs, location labels).
 */
export function parseGs1(raw: string, today: Date = new Date()): Gs1Fields | null {
  let s = raw.trim();
  const aim = /^\](C1|e0|d2|Q3|J1)/.exec(s);
  if (aim) s = s.slice(3);

  const values = new Map<string, string>();
  if (s.startsWith('(')) {
    for (const m of s.matchAll(/\((\d{2})\)([^(]*)/g)) values.set(m[1]!, m[2]!.trim());
  } else {
    const startsWithGtin = /^01\d{14}/.test(s) && isValidGtin(s.slice(2, 16));
    if (!aim && !s.includes(GROUP_SEPARATOR) && !startsWithGtin) return null;
    let pos = 0;
    while (pos < s.length) {
      if (s[pos] === GROUP_SEPARATOR) {
        pos++;
        continue;
      }
      const ai = s.slice(pos, pos + 2);
      const fixed = FIXED_LENGTH[ai];
      if (fixed !== undefined) {
        values.set(ai, s.slice(pos + 2, pos + 2 + fixed));
        pos += 2 + fixed;
      } else if (VARIABLE_LENGTH.has(ai)) {
        const end = s.indexOf(GROUP_SEPARATOR, pos + 2);
        values.set(ai, s.slice(pos + 2, end < 0 ? s.length : end));
        pos = end < 0 ? s.length : end + 1;
      } else {
        break; // unknown AI: its length is unknown, stop rather than misread
      }
    }
  }

  const gtin = values.get('01') ?? values.get('02');
  const fields: Gs1Fields = {
    ...(gtin && isValidGtin(gtin) ? { gtin } : {}),
    ...(values.get('10') ? { lot: values.get('10')! } : {}),
    ...(values.get('21') ? { serial: values.get('21')! } : {}),
    ...optionalDate('productionDate', values.get('11'), today),
    ...optionalDate('bestBefore', values.get('15'), today),
    ...optionalDate('expiry', values.get('17'), today),
    ...(values.get('37') && /^\d+$/.test(values.get('37')!) ? { count: Number(values.get('37')) } : {}),
  };
  return Object.keys(fields).length > 0 ? fields : null;
}

function optionalDate<K extends string>(key: K, yymmdd: string | undefined, today: Date): { [P in K]?: IsoDate } {
  const date = yymmdd === undefined ? undefined : gs1Date(yymmdd, today);
  return (date === undefined ? {} : { [key]: date }) as { [P in K]?: IsoDate };
}

const pad2 = (n: number): string => String(n).padStart(2, '0');

/**
 * YYMMDD → YYYY-MM-DD. The century puts the date within −49…+50 years of today
 * (GS1 General Specifications 7.12); day 00 means the last day of the month.
 */
export function gs1Date(yymmdd: string, today: Date = new Date()): IsoDate | undefined {
  if (!/^\d{6}$/.test(yymmdd)) return undefined;
  const yy = Number(yymmdd.slice(0, 2));
  const mm = Number(yymmdd.slice(2, 4));
  const dd = Number(yymmdd.slice(4, 6));
  if (mm < 1 || mm > 12) return undefined;
  const currentYear = today.getUTCFullYear();
  let century = currentYear - (currentYear % 100);
  const diff = yy - (currentYear % 100);
  if (diff >= 51) century -= 100;
  else if (diff <= -50) century += 100;
  const year = century + yy;
  const lastDay = new Date(Date.UTC(year, mm, 0)).getUTCDate();
  const day = dd === 0 ? lastDay : dd;
  if (day > lastDay) return undefined;
  return `${year}-${pad2(mm)}-${pad2(day)}`;
}
